/**
 * The Thicket RPG Lab's art. The Lab page itself (art/lab/rpg-lab.html, served
 * at /lab/rpg-lab) runs in a hidden frame, and `labBridge.js` lets the app
 * use its drawing code: every scene, native tree, wildflower, bed plant, weed,
 * companion and wild visitor, with its water, fireflies and dusk. The garden
 * hands over plain data and draws back a finished frame.
 *
 * Unlike the placeholder, this art draws the whole scene itself (`scene`), so
 * plants sort in with the Lab's rocks, trees and animals.
 */
import { BIOMES, parseMap, setBiomeMap, type Biome, type BiomeMap, type GameState, type SceneEdits } from '@thicket/core';
import type { Anchor, Ctx, GardenArt, SceneOptions } from '../types.js';
import bridgeSource from './labBridge.js?raw';

/** The app's biomes by their Lab scene keys. */
export const LAB_KEY: Record<Biome, string> = {
  meadow: 'meadow', wetland: 'wetland', jungleRuins: 'jungle', canyon: 'desert', pineRidge: 'ridge',
  woodlandLake: 'woodland', coast: 'coast', mountainSummit: 'mountain', sacredGrove: 'grove',
};

type Item =
  | { id: string; t: 'tree'; species: string; stage: string; sparse: boolean; x: number; y: number }
  | { id: string; t: 'wild'; pace: string; bloomed: boolean; last: boolean; drooping: boolean; x: number; y: number }
  | { id: string; t: 'flower'; seed: string; x: number; y: number }
  | { id: string; t: 'bed'; pace: string; stage: string; plot: number }
  | { id: string; t: 'weed'; plot: number | null; n: number }
  | { id: string; t: 'animal'; kind: string; home: string | null };

export interface Piece { id: string; name: string; x: number; y: number }
export interface BankPiece extends Piece { png: string }

interface Bridge {
  prepare(key: string, holder: Holder, trees: boolean, edits?: SceneEdits): { rows: string[]; blocked: [number, number][]; bed: { x: number; y: number; w: number; h: number } | null; float: boolean };
  visit(key: string, holder: Holder, trees: boolean, edits?: SceneEdits): void;
  pieceAt(x: number, y: number): (Piece & { w: number; h: number; ax: number; ay: number }) | null;
  movePiece(id: string, x: number, y: number): void;
  pieces(): Piece[];
  bank(key: string, trees: boolean, edits?: SceneEdits): BankPiece[];
  compose(key: string, items: Item[]): void;
  render(frame: number, dusk: boolean): HTMLCanvasElement;
  sprite(spec: Record<string, unknown>): { canvas: HTMLCanvasElement; ax: number; ay: number } | null;
  readonly key: string | null;
}

let bridge: Bridge | null = null;
let loading: Promise<void> | null = null;

/** Where the Lab page is served (the service worker keeps a copy, so the art works offline). */
export const LAB_URL = '/lab/rpg-lab';

/**
 * Loads the Lab page and its bridge; the art can't draw until this resolves. If it can't
 * load (offline with no saved copy, or the page is missing), the promise rejects soon after
 * the frame does, and the next call tries again from scratch.
 */
export function loadLab(src = LAB_URL): Promise<void> {
  if (!loading) {
    const attempt: Promise<void> = new Promise((resolve, reject) => {
      const frame = document.createElement('iframe');
      frame.title = 'Thicket RPG Lab (art engine)';
      frame.setAttribute('aria-hidden', 'true');
      frame.tabIndex = -1;
      // Kept in the page but out of sight: a frame that isn't displayed may not run its scripts.
      Object.assign(frame.style, { position: 'fixed', left: '-10000px', top: '0', width: '400px', height: '300px', border: '0', opacity: '0', pointerEvents: 'none' });
      frame.src = src;
      const started = Date.now();
      let loadedAt = 0;
      const fail = (message: string) => {
        frame.remove();
        reject(new Error(message));
      };
      const wait = () => {
        const w = frame.contentWindow as (Window & { eval(code: string): unknown; __thicket?: Bridge }) | null;
        let ready = false;
        try {
          // The Lab's tables are script-level consts, not window properties, so ask its own scope.
          ready = !!w?.eval("typeof SCENES !== 'undefined' && typeof NATIVE !== 'undefined' && typeof buildScene === 'function'");
        } catch {
          // The browser's own error page (no connection) isn't ours to look into: the Lab didn't arrive.
          if (loadedAt) return fail('The Lab art could not load');
        }
        if (w && ready) {
          try {
            w.eval(bridgeSource);
            bridge = w.__thicket!;
            resolve();
          } catch (err) {
            frame.remove();
            reject(err);
          }
          return;
        }
        // The Lab sets up its tables while the page loads; a page that loaded without them isn't the Lab.
        if (loadedAt && Date.now() - loadedAt > 10_000) return fail('The Lab art could not load');
        if (Date.now() - started > 60_000) return fail('The Lab art took too long to load');
        setTimeout(wait, 100);
      };
      frame.addEventListener('load', () => {
        loadedAt = Date.now();
        wait();
      }, { once: true });
      frame.addEventListener('error', () => fail('The Lab art could not load'), { once: true });
      document.body.append(frame);
    });
    loading = attempt;
    attempt.catch(() => {
      if (loading === attempt) loading = null;
    });
  }
  return loading;
}

export const labReady = () => bridge !== null;

/** The habit plants live in the meadow's stone bed; everywhere else they ride in the flower cart. */
type Holder = 'bed' | 'cart';
const holderOf = (b: Biome): Holder => (b === 'meadow' ? 'bed' : 'cart');

/**
 * Things the dev panel can change about how the scene looks. `trees` puts back the Lab's
 * free-standing scenery trees; a starting scene has none, so only the player's own trees grow.
 */
export const labLook = { dusk: false, trees: false };

const maps = new Map<string, BiomeMap & { bedRect: { x: number; y: number; w: number; h: number } | null; float: boolean }>();

/** The player's scenery edits, as last seen by the garden; the planting maps follow them. */
let edits: Partial<Record<Biome, SceneEdits>> = {};
const NO_EDITS: SceneEdits = { moves: {}, hidden: [] };
const editsOf = (biome: Biome): SceneEdits => edits[biome] ?? NO_EDITS;
const editSig = (e: SceneEdits) => (Object.keys(e.moves).length || e.hidden.length ? JSON.stringify([e.moves, e.hidden]) : '0');

/** Takes the player's current scenery edits; when they change, the planting maps are re-registered. */
export function useEdits(next: Partial<Record<Biome, SceneEdits>> | undefined) {
  const n = next ?? {};
  let changed = false;
  for (const b of BIOMES) if (editSig(n[b] ?? NO_EDITS) !== editSig(edits[b] ?? NO_EDITS)) changed = true;
  if (!changed) return;
  edits = n;
  if (bridge) for (const b of BIOMES) setBiomeMap(b, mapOf(b));
}

function mapOf(biome: Biome) {
  const id = `${biome}|${labLook.trees}|${editSig(editsOf(biome))}`;
  let m = maps.get(id);
  if (!m) {
    const p = bridge!.prepare(LAB_KEY[biome], holderOf(biome), labLook.trees, editsOf(biome));
    m = { ...parseMap(p.rows, p.blocked), bedRect: p.bed, float: p.float };
    maps.set(id, m);
  }
  return m;
}

/** Scenery editing: what's under a scene point, a live drag preview, and the bank of put-away pieces. */
export const scenery = {
  pieceAt: (x: number, y: number) => bridge?.pieceAt(Math.floor(x), Math.floor(y)) ?? null,
  move: (id: string, x: number, y: number) => bridge?.movePiece(id, Math.round(x), Math.round(y)),
  bank: (biome: Biome) => bridge?.bank(LAB_KEY[biome], labLook.trees, editsOf(biome)) ?? [],
  pieces: () => bridge?.pieces() ?? [],
};

/** Shows or hides the scenery trees; planting spots change with them, so the shared maps are updated too. */
export function setSceneryTrees(on: boolean) {
  labLook.trees = on;
  newVisit();
  for (const b of BIOMES) setBiomeMap(b, mapOf(b));
}

let visited: string | null = null;
const visitId = (biome: Biome) => `${LAB_KEY[biome]}|${labLook.trees}|${editSig(editsOf(biome))}`;
/** Rolls a fresh set of wild visitors next frame, as opening the biome again would. */
export function newVisit() {
  visited = null;
}

function itemsOf(s: GameState, biome: Biome, today: string): Item[] {
  const items: Item[] = [];
  const tasks = new Map(s.tasks.map((t) => [t.id, t]));
  const bedPlots = new Map<string, number>();
  for (const p of s.plants) {
    if (p.goneAt) continue;
    if (p.kind === 'bed') {
      // The cart carries the habit plants, so they show in every biome.
      if (p.plot === null) continue;
      items.push({ id: p.id, t: 'bed', pace: p.pace, stage: p.stage, plot: p.plot });
      if (p.taskId) bedPlots.set(p.taskId, p.plot);
      continue;
    }
    if (p.biome !== biome || p.x === null || p.y === null || p.plot !== null) continue;
    if (p.kind === 'tree') items.push({ id: p.id, t: 'tree', species: p.species, stage: p.stage, sparse: p.leaves === 'sparse', x: p.x, y: p.y });
    else if (p.kind === 'wildflower') {
      const task = p.taskId ? tasks.get(p.taskId) : undefined;
      const due = task && task.type === 'todo' ? task.dueDate : null;
      items.push({ id: p.id, t: 'wild', pace: p.pace, bloomed: p.bloomed, last: p.bloomed && (p.bloomDaysLeft ?? 9) <= 1, drooping: !p.bloomed && !!due && due < today, x: p.x, y: p.y });
    } else if (p.kind === 'flower') items.push({ id: p.id, t: 'flower', seed: p.species, x: p.x, y: p.y });
  }
  {
    const count = new Map<string, number>();
    for (const p of s.plants) {
      if (p.goneAt || p.kind !== 'weed' || !p.taskId) continue;
      const n = count.get(p.taskId) ?? 0;
      count.set(p.taskId, n + 1);
      items.push({ id: p.id, t: 'weed', plot: bedPlots.get(p.taskId) ?? null, n });
    }
  }
  for (const a of s.inventory) {
    if (a.type !== 'animal' || a.biome !== biome) continue;
    items.push({ id: a.id, t: 'animal', kind: a.kind, home: a.homePlantId ?? null });
  }
  return items;
}

function stamp(ctx: Ctx, spec: Record<string, unknown>, at: Anchor) {
  const s = bridge?.sprite(spec);
  if (s) ctx.drawImage(s.canvas, Math.round(at.x - s.ax), Math.round(at.y - s.ay));
}

const blank = (() => {
  let c: HTMLCanvasElement | null = null;
  return () => (c ??= Object.assign(document.createElement('canvas'), { width: 384, height: 256 }));
})();

/** Small pictures for the tab bar, drawn from the Lab's sprites once it has loaded. */
/** The Field Journal tab: an open field guide with a leaf on its page, in the meadow's colours. */
function journalIcon(ctx: Ctx) {
  const px = (c: string, x: number, y: number, w = 1, h = 1) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
  const O = '#16281f', P = '#eef0e2', S = '#cbd1bd', L = '#a9b39a';
  // green cover peeking under the pages
  px(O, 1, 8, 22, 12); px('#437c34', 2, 9, 20, 10);
  // pages, dipping toward the spine
  px(O, 2, 5, 9, 13); px(O, 13, 5, 9, 13); px(O, 11, 7, 2, 12);
  px(P, 3, 6, 8, 11); px(P, 13, 6, 8, 11); px(P, 11, 8, 2, 10);
  px(S, 10, 6, 1, 11); px(S, 13, 6, 1, 11); px(L, 11, 8, 2, 10);
  // left page: a leaf
  px('#5a972c', 6, 8, 2, 1); px('#5a972c', 5, 9, 4, 1); px('#5a972c', 4, 10, 6, 2); px('#8ab244', 5, 10, 2, 1);
  px('#5a972c', 5, 12, 4, 1); px('#5a972c', 6, 13, 2, 1); px('#2c4f35', 7, 14, 1, 2);
  // right page: notes
  px(L, 15, 8, 5, 1); px(L, 15, 10, 4, 1); px(L, 15, 12, 5, 1); px(L, 15, 14, 3, 1);
  // bookmark ribbon
  px('#b94a74', 17, 17, 2, 4); px(O, 17, 20, 1, 1);
}

export function tabIcon(ctx: Ctx, which: 'habits' | 'dailies' | 'todos' | 'garden' | 'journal') {
  if (which === 'journal') return journalIcon(ctx);
  if (which === 'habits') stamp(ctx, { t: 'bed', pace: 'medium', stage: 'large' }, { x: 12, y: 22 });
  else if (which === 'dailies') stamp(ctx, { t: 'tree', key: 'meadow', species: 'oak', stage: 'small', sparse: false }, { x: 12, y: 23 });
  else if (which === 'todos') stamp(ctx, { t: 'wild', pace: 'hard', stage: 'bloom' }, { x: 12, y: 23 });
  else {
    const s = bridge?.sprite({ t: 'cart' });
    if (s) { ctx.save(); ctx.scale(0.5, 0.5); ctx.drawImage(s.canvas, Math.round(24 - s.ax), Math.round(46 - s.ay)); ctx.restore(); }
  }
}

export const labArt: GardenArt = {
  name: 'lab',
  map: (biome) => mapOf(biome),
  background: () => blank(),
  scene(ctx: Ctx, s: GameState, biome: Biome, o: SceneOptions) {
    if (!bridge) return;
    const key = LAB_KEY[biome];
    useEdits(s.profile.scenes);
    mapOf(biome);
    if (visited !== visitId(biome)) {
      bridge.visit(key, holderOf(biome), labLook.trees, editsOf(biome));
      visited = visitId(biome);
    }
    bridge.compose(key, itemsOf(s, biome, o.today));
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(bridge.render(o.reduce ? 0 : o.tick, labLook.dusk), 0, 0);
  },
  tree: (ctx, t) => stamp(ctx, { t: 'tree', key: LAB_KEY[t.biome], species: t.species, stage: t.stage, sparse: t.sparse }, t),
  treePerch: (_stage, at) => ({ top: [at.x, at.y - 30], mid: [at.x, at.y - 18] }),
  bud: (ctx, a) => stamp(ctx, { t: 'wild', pace: a.pace, stage: a.drooping ? 'droop' : 'bud' }, a),
  bloom: (ctx, a) => stamp(ctx, { t: 'wild', pace: a.pace, stage: a.daysLeft <= 1 ? 'seed' : 'bloom' }, a),
  flower: (ctx, a) => stamp(ctx, { t: 'flower', seed: a.seed }, a),
  bedPlant: (ctx, a) => stamp(ctx, { t: 'bed', pace: a.pace ?? (['trivial', 'easy', 'medium', 'hard'] as const)[a.seed % 4], stage: a.stage }, a),
  weed: (ctx, a) => stamp(ctx, { t: 'weed', kind: 'dandelion' }, a),
  animal: (ctx, a) => stamp(ctx, { t: 'animal', kind: a.kind }, a),
  animalSize: () => ({ w: 14, h: 14 }),
};

const previews = new Map<Biome, string>();

/** A still of a biome as a new player would first see it (nothing planted), for choosing a starting place. */
export function labPreview(biome: Biome, s: GameState): string | null {
  if (!bridge) return null;
  let url = previews.get(biome);
  if (!url) {
    const key = LAB_KEY[biome];
    mapOf(biome);
    bridge.visit(key, holderOf(biome), labLook.trees);
    bridge.compose(key, itemsOf({ ...s, plants: [], inventory: [] }, biome, '2000-01-01'));
    url = bridge.render(0, false).toDataURL();
    previews.set(biome, url);
    // The garden will visit its own biome again before drawing.
    visited = null;
  }
  return url;
}
